import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/LoginPage.css';

interface TenantBranding {
  name: string;
  logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  website_url?: string | null;
}

const DEFAULT_BRANDING: TenantBranding = {
  name: 'Adroit',
  logo_url: null,
  primary_color: '#0D9488',
  secondary_color: '#14B8A6'
};

const ANOMER_BRANDING: TenantBranding = {
  name: 'Anomer Bio',
  logo_url: '/anomer-logo.png',
  primary_color: '#0A2F73',
  secondary_color: '#2867A8'
};

const AUTIFONY_BRANDING: TenantBranding = {
  name: 'Autifony Therapeutics',
  logo_url: '/autifony-logo.svg',
  primary_color: '#1F4280',
  secondary_color: '#638C4B'
};

const BIOASCENT_BRANDING: TenantBranding = {
  name: 'BioAscent Demo',
  logo_url: '/bioascent-logo.png',
  primary_color: '#1C284C',
  secondary_color: '#BC5D49'
};

const RIME_BRANDING: TenantBranding = {
  name: 'RIME Therapeutics',
  logo_url: '/rime-logo-full-light.webp',
  primary_color: '#DD785E',
  secondary_color: '#59ABA9'
};

const IRIS_BRANDING: TenantBranding = {
  name: 'Iris Scientific',
  logo_url: '/iris-scientific-logo.png',
  primary_color: '#0D2B4E',
  secondary_color: '#12A8C9',
  website_url: 'https://www.iris-scientific.com/'
};

const NOMIC_BRANDING: TenantBranding = {
  name: 'Nomic Bio',
  logo_url: '/nomic-logo.svg',
  primary_color: '#4A4AD8',
  secondary_color: '#4AD889'
};

const STEREO_BRANDING: TenantBranding = {
  name: 'Stereo Biotherapeutics',
  logo_url: '/stereo-logo-dark.svg',
  primary_color: '#004BA8',
  secondary_color: '#00D9C0'
};

const AMPLITUDE_BRANDING: TenantBranding = {
  name: 'Amplitude Ventures / Pre-Amp',
  logo_url: '/amplitude-logo-invert.svg',
  primary_color: '#02855E',
  secondary_color: '#7EDE64'
};

const isBioAscentHost = (hostname: string): boolean =>
  hostname === 'bioascent-demo.adroitai.ai' || hostname.startsWith('bioascent-demo.') ||
  hostname === 'bioascent-preview.adroitai.ai' || hostname.startsWith('bioascent-preview.');

const isAutifonyHost = (hostname: string): boolean =>
  hostname === 'autifony.adroitai.ai' || hostname.startsWith('autifony.');

const isRimeHost = (hostname: string): boolean =>
  hostname === 'rime.adroitai.ai' || hostname.startsWith('rime.');

const isIrisHost = (hostname: string): boolean =>
  hostname === 'iris-scientific.adroitai.ai' || hostname.startsWith('iris-scientific.');

const isNomicHost = (hostname: string): boolean =>
  hostname === 'nomic.adroitai.ai' || hostname.startsWith('nomic.');

const isStereoHost = (hostname: string): boolean =>
  hostname === 'stereo.adroitai.ai' || hostname.startsWith('stereo.');

const isAmplitudeHost = (hostname: string): boolean =>
  hostname === 'amplitude.adroitai.ai' || hostname.startsWith('amplitude.');

const isAnomerHost = (hostname: string): boolean =>
  hostname === 'anomer-demo.adroitai.ai' || hostname.startsWith('anomer-demo.') ||
  hostname === 'anomer-qa.adroitai.ai' || hostname.startsWith('anomer-qa.');

const isAnomerQaHost = (hostname: string): boolean =>
  hostname === 'anomer-qa.adroitai.ai' || hostname.startsWith('anomer-qa.');

const isIPv4Host = (hostname: string): boolean => /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname);

// Scientifically accurate node types with real drug discovery examples
const nodeTypes = [
  { type: 'molecule', color: '#3b82f6', label: 'Gefitinib', description: 'EGFR inhibitor' },
  { type: 'molecule', color: '#3b82f6', label: 'Erlotinib', description: 'Kinase inhibitor' },
  { type: 'molecule', color: '#3b82f6', label: 'Imatinib', description: 'BCR-ABL inhibitor' },
  { type: 'target', color: '#ef4444', label: 'EGFR', description: 'P00533' },
  { type: 'target', color: '#ef4444', label: 'KRAS', description: 'P01116' },
  { type: 'target', color: '#ef4444', label: 'BCR-ABL', description: 'Fusion protein' },
  { type: 'protein', color: '#8b5cf6', label: 'GRB2', description: 'Adaptor protein' },
  { type: 'protein', color: '#8b5cf6', label: 'SOS1', description: 'GEF protein' },
  { type: 'pathway', color: '#10b981', label: 'ERK1/ERK2 pathway', description: 'R-HSA-5684996' },
  { type: 'pathway', color: '#10b981', label: 'PI3K Cascade', description: 'R-HSA-109704' },
  { type: 'patent', color: '#f59e0b', label: 'US7772243', description: 'Gefitinib claims' },
  { type: 'patent', color: '#f59e0b', label: 'WO2005021531', description: 'EGFR inhibitors' },
  { type: 'assay', color: '#ec4899', label: 'IC50 = 33nM', description: 'EGFR kinase' },
  { type: 'structure', color: '#06b6d4', label: '2ITY', description: 'EGFR-Gefitinib' },
];

// Real biological relationships
const relationships = [
  { from: 'Gefitinib', to: 'EGFR', type: 'inhibits' },
  { from: 'Erlotinib', to: 'EGFR', type: 'inhibits' },
  { from: 'Imatinib', to: 'BCR-ABL', type: 'inhibits' },
  { from: 'EGFR', to: 'GRB2', type: 'activates' },
  { from: 'GRB2', to: 'SOS1', type: 'binds' },
  { from: 'SOS1', to: 'KRAS', type: 'activates' },
  { from: 'EGFR', to: 'ERK1/ERK2 pathway', type: 'member' },
  { from: 'KRAS', to: 'ERK1/ERK2 pathway', type: 'member' },
  { from: 'EGFR', to: 'PI3K Cascade', type: 'member' },
  { from: 'Gefitinib', to: 'US7772243', type: 'claimed' },
  { from: 'EGFR', to: 'WO2005021531', type: 'target' },
  { from: 'Gefitinib', to: 'IC50 = 33nM', type: 'measured' },
  { from: 'IC50 = 33nM', to: 'EGFR', type: 'against' },
  { from: 'EGFR', to: '2ITY', type: 'structure' },
  { from: 'Gefitinib', to: '2ITY', type: 'bound' },
];

interface NetworkNode {
  type: string;
  color: string;
  label: string;
  description: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  pulsePhase: number;
}

interface NetworkEdge {
  from: string;
  to: string;
  type: string;
  fromNode: NetworkNode;
  toNode: NetworkNode;
  particles: { progress: number; speed: number }[];
  lastParticle: number;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, loading } = useAuth();
  const anomerLogin = isAnomerHost(window.location.hostname);
  const anomerQaLogin = isAnomerQaHost(window.location.hostname);
  const autifonyLogin = isAutifonyHost(window.location.hostname);
  const bioAscentLogin = isBioAscentHost(window.location.hostname);
  const rimeLogin = isRimeHost(window.location.hostname);
  const irisLogin = isIrisHost(window.location.hostname);
  const nomicLogin = isNomicHost(window.location.hostname);
  const stereoLogin = isStereoHost(window.location.hostname);
  const amplitudeLogin = isAmplitudeHost(window.location.hostname);
  const tenantLogin = anomerLogin || autifonyLogin || bioAscentLogin || rimeLogin || irisLogin || nomicLogin || stereoLogin || amplitudeLogin;
  // Determine where to send the user after login.
  // Only honour saved paths that are real app routes (not auth pages, not blank).
  const savedPath = (location.state as any)?.from || localStorage.getItem('discoverant_last_path');
  const BLOCKED = ['/login', '/set-password', '/shared'];
  const redirectTo = savedPath && savedPath.startsWith('/') && !BLOCKED.some(b => savedPath.startsWith(b))
    ? savedPath
    : '/chemistry';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [branding, setBranding] = useState<TenantBranding>(() =>
    bioAscentLogin ? BIOASCENT_BRANDING
      : anomerLogin ? ANOMER_BRANDING
      : autifonyLogin ? AUTIFONY_BRANDING
      : rimeLogin ? RIME_BRANDING
      : irisLogin ? IRIS_BRANDING
      : nomicLogin ? NOMIC_BRANDING
      : stereoLogin ? STEREO_BRANDING
      : amplitudeLogin ? AMPLITUDE_BRANDING
      : DEFAULT_BRANDING
  );
  const [showForgotMsg, setShowForgotMsg] = useState(false);

  // Canvas animation refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const nodesRef = useRef<NetworkNode[]>([]);
  const edgesRef = useRef<NetworkEdge[]>([]);

  // If already authenticated, redirect to app
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, redirectTo]);

  // Fetch tenant branding based on subdomain or query param
  useEffect(() => {
    const fetchBranding = async () => {
      // Get subdomain from hostname (e.g., "acme" from "acme.adroitai.ai")
      // Or use ?tenant=acme query param for local testing
      const hostname = window.location.hostname;
      if (isBioAscentHost(hostname)) {
        setBranding(BIOASCENT_BRANDING);
        return;
      }
      if (isAnomerHost(hostname) && !isAnomerQaHost(hostname)) {
        setBranding(ANOMER_BRANDING);
        return;
      }
      if (isAutifonyHost(hostname)) {
        setBranding(AUTIFONY_BRANDING);
        return;
      }
      if (isRimeHost(hostname)) {
        setBranding(RIME_BRANDING);
        return;
      }
      if (isIrisHost(hostname)) {
        setBranding(IRIS_BRANDING);
        return;
      }
      if (isNomicHost(hostname)) {
        setBranding(NOMIC_BRANDING);
        return;
      }
      if (isStereoHost(hostname)) {
        setBranding(STEREO_BRANDING);
        return;
      }
      if (isAmplitudeHost(hostname)) {
        setBranding(AMPLITUDE_BRANDING);
        return;
      }
      const parts = hostname.split('.');
      let subdomain = !isIPv4Host(hostname) && parts.length >= 3 ? parts[0] : null;

      // Query param override for local testing
      const tenantParam = searchParams.get('tenant');
      if (tenantParam) {
        subdomain = tenantParam;
      }

      // Skip if default "app" subdomain or no subdomain
      if (!subdomain || subdomain === 'app' || subdomain === 'localhost' || subdomain === 'dev') {
        return;
      }

      try {
        const response = await fetch(`/api/v1/tenants/by-subdomain/${subdomain}`);
        if (response.ok) {
          const data: TenantBranding = await response.json();
          setBranding(data);
        }
        // 404 is expected when no tenant record exists — fall back to defaults silently
      } catch {
        // Network errors — fall back silently
      }
    };

    fetchBranding();
  }, [searchParams]);

  useEffect(() => {
    if (!anomerLogin) return;
    const previousTitle = document.title;
    document.title = anomerQaLogin ? 'Anomer QA Test Environment' : 'Anomer Bio Research Platform';
    return () => {
      document.title = previousTitle;
    };
  }, [anomerLogin, anomerQaLogin]);

  useEffect(() => {
    if (!autifonyLogin) return;
    const previousTitle = document.title;
    document.title = 'Autifony Therapeutics Research Platform';
    return () => { document.title = previousTitle; };
  }, [autifonyLogin]);

  useEffect(() => {
    if (!bioAscentLogin) return;
    const previousTitle = document.title;
    document.title = 'BioAscent Demonstration Platform';
    return () => { document.title = previousTitle; };
  }, [bioAscentLogin]);

  useEffect(() => {
    const title = rimeLogin
      ? 'RIME Therapeutics Research Workspace'
      : nomicLogin
        ? 'Nomic Bio Proteomics Workspace'
        : stereoLogin
          ? 'Stereo Biotherapeutics Research Workspace'
          : amplitudeLogin
            ? 'Amplitude Ventures Portfolio Workspace'
            : null;
    if (!title) return;
    const previousTitle = document.title;
    document.title = title;
    return () => { document.title = previousTitle; };
  }, [rimeLogin, nomicLogin, stereoLogin, amplitudeLogin]);

  // Canvas-based network animation with physics simulation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateCanvasSize();

    const width = canvas.width;
    const height = canvas.height;

    // Initialize nodes with physics
    nodesRef.current = nodeTypes.map((node) => ({
      ...node,
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: node.type === 'molecule' ? 28 : node.type === 'target' ? 26 : node.type === 'pathway' ? 24 : 20,
      pulsePhase: Math.random() * Math.PI * 2,
    }));

    // Create edges from relationships
    edgesRef.current = relationships.map(rel => {
      const fromNode = nodesRef.current.find(n => n.label === rel.from);
      const toNode = nodesRef.current.find(n => n.label === rel.to);
      if (!fromNode || !toNode) return null;
      return {
        ...rel,
        fromNode,
        toNode,
        particles: [] as { progress: number; speed: number }[],
        lastParticle: 0,
      };
    }).filter((e): e is NetworkEdge => e !== null);

    const animate = () => {
      const currentWidth = canvas.width;
      const currentHeight = canvas.height;

      ctx.fillStyle = 'rgba(13, 42, 48, 1)';
      ctx.fillRect(0, 0, currentWidth, currentHeight);

      // Draw subtle grid pattern
      ctx.strokeStyle = 'rgba(45, 212, 191, 0.03)';
      ctx.lineWidth = 1;
      const gridSize = 60;
      for (let x = 0; x < currentWidth; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, currentHeight);
        ctx.stroke();
      }
      for (let y = 0; y < currentHeight; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(currentWidth, y);
        ctx.stroke();
      }

      const nodes = nodesRef.current;
      const edges = edgesRef.current;
      const time = Date.now() * 0.001;

      // Physics simulation
      nodes.forEach(node => {
        // Repel from center (login box area)
        const centerX = currentWidth / 2;
        const centerY = currentHeight / 2;
        const dx = node.x - centerX;
        const dy = node.y - centerY;
        const distToCenter = Math.sqrt(dx * dx + dy * dy);
        const minDist = 350;

        if (distToCenter < minDist) {
          const force = (minDist - distToCenter) * 0.01;
          node.vx += (dx / distToCenter) * force;
          node.vy += (dy / distToCenter) * force;
        }

        // Gentle attraction to keep nodes on screen
        if (node.x < 100) node.vx += 0.1;
        if (node.x > currentWidth - 100) node.vx -= 0.1;
        if (node.y < 100) node.vy += 0.1;
        if (node.y > currentHeight - 100) node.vy -= 0.1;

        // Node-node repulsion
        nodes.forEach(other => {
          if (other === node) return;
          const ndx = node.x - other.x;
          const ndy = node.y - other.y;
          const dist = Math.sqrt(ndx * ndx + ndy * ndy);
          const minNodeDist = 100;
          if (dist < minNodeDist && dist > 0) {
            const force = (minNodeDist - dist) * 0.02;
            node.vx += (ndx / dist) * force;
            node.vy += (ndy / dist) * force;
          }
        });

        // Edge attraction (connected nodes want to be closer)
        edges.forEach(edge => {
          if (edge.fromNode === node || edge.toNode === node) {
            const other = edge.fromNode === node ? edge.toNode : edge.fromNode;
            const edx = other.x - node.x;
            const edy = other.y - node.y;
            const dist = Math.sqrt(edx * edx + edy * edy);
            const idealDist = 180;
            if (dist > idealDist) {
              const force = (dist - idealDist) * 0.001;
              node.vx += (edx / dist) * force;
              node.vy += (edy / dist) * force;
            }
          }
        });

        // Apply velocity with damping
        node.vx *= 0.98;
        node.vy *= 0.98;
        node.x += node.vx;
        node.y += node.vy;

        // Clamp to screen
        node.x = Math.max(60, Math.min(currentWidth - 60, node.x));
        node.y = Math.max(60, Math.min(currentHeight - 60, node.y));
      });

      // Draw edges with data flow particles
      edges.forEach(edge => {
        const { fromNode, toNode } = edge;

        // Draw edge line
        const gradient = ctx.createLinearGradient(fromNode.x, fromNode.y, toNode.x, toNode.y);
        gradient.addColorStop(0, fromNode.color + '60');
        gradient.addColorStop(1, toNode.color + '60');

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(fromNode.x, fromNode.y);
        ctx.lineTo(toNode.x, toNode.y);
        ctx.stroke();

        // Spawn particles periodically
        if (time - edge.lastParticle > 2 + Math.random() * 3) {
          edge.particles.push({
            progress: 0,
            speed: 0.003 + Math.random() * 0.002,
          });
          edge.lastParticle = time;
        }

        // Update and draw particles
        edge.particles = edge.particles.filter(p => {
          p.progress += p.speed;
          if (p.progress > 1) return false;

          const px = fromNode.x + (toNode.x - fromNode.x) * p.progress;
          const py = fromNode.y + (toNode.y - fromNode.y) * p.progress;

          const particleColor = p.progress < 0.5 ? fromNode.color : toNode.color;
          ctx.fillStyle = particleColor;
          ctx.beginPath();
          ctx.arc(px, py, 3, 0, Math.PI * 2);
          ctx.fill();

          // Glow
          ctx.shadowColor = particleColor;
          ctx.shadowBlur = 8;
          ctx.fill();
          ctx.shadowBlur = 0;

          return true;
        });
      });

      // Draw nodes
      nodes.forEach(node => {
        const pulse = Math.sin(time * 2 + node.pulsePhase) * 0.15 + 1;
        const drawRadius = node.radius * pulse;

        // Outer glow
        const glowGradient = ctx.createRadialGradient(
          node.x, node.y, drawRadius * 0.5,
          node.x, node.y, drawRadius * 2
        );
        glowGradient.addColorStop(0, node.color + '40');
        glowGradient.addColorStop(1, 'transparent');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, drawRadius * 2, 0, Math.PI * 2);
        ctx.fill();

        // Node circle
        ctx.fillStyle = node.color + '30';
        ctx.strokeStyle = node.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(node.x, node.y, drawRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Inner highlight
        ctx.fillStyle = node.color + '60';
        ctx.beginPath();
        ctx.arc(node.x - drawRadius * 0.2, node.y - drawRadius * 0.2, drawRadius * 0.3, 0, Math.PI * 2);
        ctx.fill();

        // Label
        ctx.font = '600 11px "SF Pro Display", -apple-system, sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(node.label, node.x, node.y + drawRadius + 16);

        // Type badge
        ctx.font = '500 9px "SF Pro Display", -apple-system, sans-serif';
        ctx.fillStyle = node.color + 'cc';
        ctx.fillText(node.type.toUpperCase(), node.x, node.y + drawRadius + 28);
      });

      // Draw legend in corner
      const legendX = 30;
      let legendY = currentHeight - 200;
      ctx.font = '600 12px "SF Pro Display", -apple-system, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.textAlign = 'left';
      ctx.fillText('Data Types', legendX, legendY);

      const types = [
        { color: '#3b82f6', label: 'Molecules' },
        { color: '#ef4444', label: 'Targets' },
        { color: '#8b5cf6', label: 'Proteins' },
        { color: '#10b981', label: 'Pathways' },
        { color: '#f59e0b', label: 'Patents' },
        { color: '#ec4899', label: 'Assays' },
        { color: '#06b6d4', label: 'Structures' },
      ];

      ctx.font = '500 10px "SF Pro Display", -apple-system, sans-serif';
      types.forEach((t) => {
        legendY += 22;
        ctx.fillStyle = t.color;
        ctx.beginPath();
        ctx.arc(legendX + 6, legendY - 3, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.6)';
        ctx.fillText(t.label, legendX + 18, legendY);
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [loading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(email, password, undefined, rememberMe);

    if (result.success) {
      // AuthContext updates token and user state asynchronously. Navigating in
      // this same tick can render PrivateRoute against the previous signed-out
      // state and bounce a valid login straight back here. The authenticated
      // state effect above performs the redirect once that state is committed.
      return;
    } else {
      setError(result.error || 'Login failed');
      setIsLoading(false);
    }
  };

  // Show nothing while checking auth status
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--content-bg)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(99, 102, 241, 0.3)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Generate CSS custom properties for branding colors
  const brandingStyles = {
    '--brand-primary': branding.primary_color,
    '--brand-secondary': branding.secondary_color,
  } as React.CSSProperties;

  return (
    <div className={`login-page${tenantLogin ? ' login-page--anomer' : ''}${autifonyLogin ? ' login-page--autifony' : ''}${rimeLogin ? ' login-page--rime' : ''}${irisLogin ? ' login-page--iris' : ''}${nomicLogin ? ' login-page--nomic' : ''}${stereoLogin ? ' login-page--stereo' : ''}${amplitudeLogin ? ' login-page--amplitude' : ''}`} style={brandingStyles}>
      {bioAscentLogin ? (
        <section className="anomer-login__brand-panel" aria-label="BioAscent demonstration workspace">
          <div className="anomer-login__home-link">
            <img src="/bioascent-logo.png" alt="BioAscent" className="anomer-login__wordmark" />
          </div>
          <div className="anomer-login__message">
            <p className="anomer-login__eyebrow">DEMONSTRATION ENVIRONMENT · SYNTHETIC DATA</p>
            <h2>Where discovery data and decisions connect.</h2>
            <p className="anomer-login__intro">
              Explore a synthetic Echo workflow across compound management, assay registration and biological results.
            </p>
          </div>
          <svg className="anomer-login__motif" viewBox="0 0 760 300" role="img" aria-label="Connected molecular building blocks">
            <defs>
              <linearGradient id="bioascentLine" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#1C284C" />
                <stop offset="100%" stopColor="#BC5D49" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#bioascentLine)" strokeWidth="5" strokeLinejoin="round">
              <path d="M80 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M212 100l42-24 42 24v48l-42 24-42-24z" />
              <path d="M344 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M476 100l42-24 42 24v48l-42 24-42-24z" />
              <path d="M608 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M164 154l48-28M296 124l48 30M428 154l48-30M560 124l48 30" strokeWidth="3" />
            </g>
          </svg>
        </section>
      ) : anomerLogin ? (
        <section className="anomer-login__brand-panel" aria-label="Anomer Bio research workspace">
          <div className="anomer-login__home-link">
            <img src="/anomer-logo.png" alt="Anomer Bio" className="anomer-login__wordmark" />
          </div>
          <div className="anomer-login__message">
            <p className="anomer-login__eyebrow">{anomerQaLogin ? 'QA TEST ENVIRONMENT' : 'PRIVATE RESEARCH ENVIRONMENT'}</p>
            <h2>{anomerQaLogin ? 'Safe testing with dummy data only.' : 'Your science, connected in one secure workspace.'}</h2>
            <p className="anomer-login__intro">
              {anomerQaLogin
                ? 'This workspace is isolated from the live Anomer tenant. Do not enter confidential or production data.'
                : "Access Anomer Bio's chemistry, biologics, project and discovery data in a workspace built around your research."}
            </p>
          </div>
          <svg className="anomer-login__motif" viewBox="0 0 760 300" role="img" aria-label="Connected molecular building blocks">
            <defs>
              <linearGradient id="anomerLine" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#0A2F73" />
                <stop offset="100%" stopColor="#7EA6D8" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#anomerLine)" strokeWidth="5" strokeLinejoin="round">
              <path d="M80 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M212 100l42-24 42 24v48l-42 24-42-24z" />
              <path d="M344 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M476 100l42-24 42 24v48l-42 24-42-24z" />
              <path d="M608 154l42-24 42 24v48l-42 24-42-24z" />
              <path d="M164 154l48-28M296 124l48 30M428 154l48-30M560 124l48 30" strokeWidth="3" />
            </g>
            <g fill="#0A2F73">
              <circle cx="122" cy="130" r="9" />
              <circle cx="254" cy="76" r="9" />
              <circle cx="386" cy="130" r="9" />
              <circle cx="518" cy="76" r="9" />
              <circle cx="650" cy="130" r="9" />
            </g>
          </svg>
        </section>
      ) : autifonyLogin ? (
        <section className="anomer-login__brand-panel autifony-login__brand-panel" aria-label="Autifony Therapeutics research workspace">
          <div className="anomer-login__home-link">
            <img src="/autifony-logo.svg" alt="Autifony Therapeutics" className="anomer-login__wordmark autifony-login__wordmark" />
          </div>
          <div className="anomer-login__message autifony-login__message">
            <p className="anomer-login__eyebrow">PRIVATE RESEARCH ENVIRONMENT</p>
            <h2>Pioneering science, connected in one workspace.</h2>
            <p className="anomer-login__intro">
              Bring your ion channel research, chemistry, assays and programmes together in Autifony Therapeutics’ secure workspace.
            </p>
          </div>
          <svg className="autifony-login__motif" viewBox="0 0 680 220" role="img" aria-label="Ion channels across a cell membrane">
            <g fill="none" stroke="#a9c3b4" strokeWidth="3" opacity="0.75">
              <path d="M18 67 Q90 48 162 67 T306 67 T450 67 T662 67" />
              <path d="M18 153 Q90 134 162 153 T306 153 T450 153 T662 153" />
            </g>
            <g fill="none" stroke="#1F4280" strokeWidth="7" strokeLinecap="round">
              <path d="M272 42 C286 61 286 85 286 107 C286 129 286 154 272 177" />
              <path d="M408 42 C394 61 394 85 394 107 C394 129 394 154 408 177" />
            </g>
            <g fill="#638C4B">
              <circle cx="340" cy="47" r="8" /><circle cx="340" cy="87" r="8" />
              <circle cx="340" cy="127" r="8" /><circle cx="340" cy="167" r="8" />
            </g>
          </svg>
        </section>
      ) : rimeLogin ? (
        <section className="anomer-login__brand-panel rime-login__brand-panel" aria-label="RIME Therapeutics research workspace">
          <div className="anomer-login__home-link">
            <img src="/rime-logo-full-light.webp" alt="RIME Therapeutics" className="anomer-login__wordmark rime-login__wordmark" />
          </div>
          <div className="anomer-login__message rime-login__message">
            <p className="anomer-login__eyebrow">PRIVATE RESEARCH ENVIRONMENT</p>
            <h2>Novel biology.<br />Actionable chemistry.</h2>
            <p className="anomer-login__intro">
              Connect target biology, screening data and structure–activity evidence in one secure discovery workspace.
            </p>
          </div>
          <div className="rime-login__nodes" aria-hidden="true">
            <span></span><span></span><span></span><span></span><span></span><span></span><span></span>
          </div>
        </section>
      ) : irisLogin ? (
        <section className="anomer-login__brand-panel iris-login__brand-panel" aria-label="Iris Scientific data and informatics workspace">
          <a className="anomer-login__home-link iris-login__home-link" href="https://www.iris-scientific.com/" target="_blank" rel="noopener noreferrer">
            <img src="/iris-scientific-logo.png" alt="" className="iris-login__logo" />
            <span className="iris-login__wordmark">
              <strong>Iris Scientific</strong>
              <small>Scientific Data &amp; Informatics</small>
            </span>
          </a>
          <div className="anomer-login__message iris-login__message">
            <p className="anomer-login__eyebrow">ASSAY DATA · CHEMINFORMATICS · AI-READY DATA</p>
            <h2>Context.<br />Data.<br />Decisions.</h2>
            <p className="anomer-login__intro">
              Connect compounds, ontology-aligned assays, instrument data and ELN records in one structured workspace.
            </p>
          </div>
          <svg className="iris-login__map" viewBox="0 0 680 255" aria-hidden="true">
            <g fill="none" stroke="rgba(255,255,255,.28)" strokeWidth="2">
              <path d="M340 126L105 62M340 126L104 197M340 126L575 62M340 126L576 197" />
            </g>
            <g className="iris-login__map-node">
              <rect x="40" y="30" width="130" height="64" rx="16" /><text x="105" y="69">COMPOUNDS</text>
              <rect x="39" y="165" width="130" height="64" rx="16" /><text x="104" y="204">PROTOCOLS</text>
              <rect x="510" y="30" width="130" height="64" rx="16" /><text x="575" y="69">ONTOLOGIES</text>
              <rect x="511" y="165" width="130" height="64" rx="16" /><text x="576" y="204">ELN</text>
            </g>
            <g className="iris-login__map-core">
              <circle cx="340" cy="126" r="61" /><circle cx="340" cy="126" r="45" />
              <text x="340" y="122">ASSAY</text><text x="340" y="143">DATA</text>
            </g>
          </svg>
        </section>
      ) : nomicLogin ? (
        <section className="anomer-login__brand-panel nomic-login__brand-panel" aria-label="Nomic Bio proteomics workspace">
          <div className="anomer-login__home-link">
            <img src="/nomic-logo.svg" alt="Nomic Bio" className="anomer-login__wordmark nomic-login__wordmark" />
          </div>
          <div className="anomer-login__message nomic-login__message">
            <p className="anomer-login__eyebrow">PRIVATE PROTEOMICS WORKSPACE</p>
            <h2>Make the proteome accessible at scale.</h2>
            <p className="anomer-login__intro">
              Connect study design, sample context, protein measurements, QC and biomarker decisions in one workspace.
            </p>
          </div>
          <div className="nomic-login__signal" aria-hidden="true">
            <span></span><span></span><span></span><span></span><span></span><span></span>
          </div>
        </section>
      ) : stereoLogin ? (
        <section className="anomer-login__brand-panel stereo-login__brand-panel" aria-label="Stereo Biotherapeutics research workspace">
          <div className="anomer-login__home-link">
            <img src="/stereo-logo-dark.svg" alt="Stereo Biotherapeutics" className="anomer-login__wordmark stereo-login__wordmark" />
          </div>
          <div className="anomer-login__message stereo-login__message">
            <p className="anomer-login__eyebrow">PRIVATE RESEARCH WORKSPACE</p>
            <h2>Broadcasting precision therapies for universal reach.</h2>
            <p className="anomer-login__intro">
              Move from pMHC target pairs through Tracker design, multispecific formats and candidate nomination.
            </p>
          </div>
          <div className="stereo-login__broadcast" aria-hidden="true"><span></span><span></span><span></span><i></i></div>
        </section>
      ) : amplitudeLogin ? (
        <section className="anomer-login__brand-panel amplitude-login__brand-panel" aria-label="Amplitude Ventures portfolio workspace">
          <div className="anomer-login__home-link">
            <img src="/amplitude-logo-invert.svg" alt="Amplitude Ventures" className="anomer-login__wordmark amplitude-login__wordmark" />
          </div>
          <div className="anomer-login__message amplitude-login__message">
            <p className="anomer-login__eyebrow">PORTFOLIO AND VENTURE CREATION WORKSPACE</p>
            <h2>Investing in human health from ideation to patient care.</h2>
            <p className="anomer-login__intro">
              Review opportunities, compare portfolio evidence, hand programmes into company workspaces and prepare diligence.
            </p>
          </div>
          <div className="amplitude-login__portfolio" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
        </section>
      ) : (
        <>
          {/* Canvas-based physics network animation */}
          <canvas ref={canvasRef} className="login-network-canvas" />
          <div className="login-page__overlay"></div>
          <div className="login-page__tagline">
            <p className="login-page__tagline-main">
              Molecules &bull; Targets &bull; Pathways &bull; Patents &bull; Assays &mdash; All Connected
            </p>
          </div>
        </>
      )}

      {/* Login card */}
      <div className={`login-card${tenantLogin ? ' login-card--anomer' : ''}${autifonyLogin ? ' login-card--autifony' : ''}${rimeLogin ? ' login-card--rime' : ''}${irisLogin ? ' login-card--iris' : ''}${nomicLogin ? ' login-card--nomic' : ''}${stereoLogin ? ' login-card--stereo' : ''}${amplitudeLogin ? ' login-card--amplitude' : ''}`}>
        <div className="login-card__header">
          {!tenantLogin && <div className="login-card__logo" style={{ background: `linear-gradient(135deg, ${branding.primary_color}1a 0%, ${branding.secondary_color}1a 100%)` }}>
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={`${branding.name} logo`}
                className="login-card__logo-img"
              />
            ) : (
              <svg viewBox="0 0 40 40" className="login-card__logo-icon">
                <defs>
                  <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={branding.primary_color} />
                    <stop offset="100%" stopColor={branding.secondary_color} />
                  </linearGradient>
                </defs>
                <circle cx="20" cy="12" r="6" fill="url(#logoGradient)" />
                <circle cx="10" cy="28" r="5" fill="url(#logoGradient)" opacity="0.8" />
                <circle cx="30" cy="28" r="5" fill="url(#logoGradient)" opacity="0.8" />
                <line x1="20" y1="12" x2="10" y2="28" stroke="url(#logoGradient)" strokeWidth="2" />
                <line x1="20" y1="12" x2="30" y2="28" stroke="url(#logoGradient)" strokeWidth="2" />
                <line x1="10" y1="28" x2="30" y2="28" stroke="url(#logoGradient)" strokeWidth="2" />
              </svg>
            )}
          </div>}
          {bioAscentLogin && <p className="login-card__overline">BIOASCENT DEMO</p>}
          {anomerLogin && <p className="login-card__overline">{anomerQaLogin ? 'ANOMER QA · TEST ENVIRONMENT' : 'ANOMER BIO'}</p>}
          {autifonyLogin && <p className="login-card__overline">AUTIFONY THERAPEUTICS</p>}
          {rimeLogin && <p className="login-card__overline">RIME THERAPEUTICS</p>}
          {irisLogin && <p className="login-card__overline">IRIS SCIENTIFIC</p>}
          {nomicLogin && <p className="login-card__overline">NOMIC BIO</p>}
          {stereoLogin && <p className="login-card__overline">STEREO BIOTHERAPEUTICS</p>}
          {amplitudeLogin && <p className="login-card__overline">AMPLITUDE VENTURES</p>}
          <h1 className="login-card__title">{tenantLogin ? 'Welcome back' : branding.name}</h1>
          <p className="login-card__subtitle">{tenantLogin ? 'Sign in to your research workspace' : 'Chemistry Research Platform'}</p>
          {!tenantLogin && branding.website_url && (
            <a
              className="login-card__website"
              href={branding.website_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {branding.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          )}
        </div>

        <form className="login-card__form" onSubmit={handleSubmit}>
          {error && (
            <div className="login-form__error">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="login-form__group">
            <label className="login-form__label" htmlFor="email">Email Address</label>
            <div className="login-form__input-wrapper">
              <svg className="login-form__icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
              <input
                id="email"
                type="email"
                className="login-form__input"
                placeholder="your.name@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                required
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          </div>

          <div className="login-form__group">
            <label className="login-form__label" htmlFor="password">Password</label>
            <div className="login-form__input-wrapper">
              <svg className="login-form__icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="login-form__input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                required
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="login-form__toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="login-form__options">
            <label className="login-form__remember">
              <input
                type="checkbox"
                className="login-form__checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Remember me</span>
            </label>
            <button type="button" className="login-form__forgot" onClick={() => setShowForgotMsg(f => !f)}>Forgot password?</button>
          </div>

          {showForgotMsg && (
            <div className="login-form__error" style={{ background: 'rgba(14, 165, 233, 0.08)', borderColor: 'rgba(14, 165, 233, 0.3)', color: '#0ea5e9' }}>
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              {anomerQaLogin ? (
                <span>Contact the QA administrator to reset this test account.</span>
              ) : bioAscentLogin ? (
                <span>Contact your demonstration administrator to reset your password.</span>
              ) : anomerLogin ? (
                <span>Contact your administrator to reset your password: info@anomer.bio</span>
              ) : autifonyLogin ? (
                <span>Contact your Autifony workspace administrator to reset your password.</span>
              ) : rimeLogin ? (
                <span>Contact your RIME workspace administrator or email info@rimetherapeutics.com.</span>
              ) : irisLogin ? (
                <span>Contact your Iris Scientific workspace administrator to reset your password.</span>
              ) : nomicLogin ? (
                <span>Contact your Nomic workspace administrator to reset your password.</span>
              ) : stereoLogin ? (
                <span>Contact your Stereo workspace administrator or email info@stereobio.com.</span>
              ) : amplitudeLogin ? (
                <span>Contact your Amplitude workspace administrator or email info@amplitudevc.com.</span>
              ) : (
                <span>Contact your administrator to reset your password, or email <a href="mailto:hello@adroitai.ai?subject=Password%20Reset%20Request" style={{ color: '#0ea5e9', textDecoration: 'underline' }}>hello@adroitai.ai</a></span>
              )}
            </div>
          )}

          <button
            type="submit"
            className={`login-form__submit ${isLoading ? 'login-form__submit--loading' : ''}`}
            disabled={isLoading}
            style={{
              background: `linear-gradient(135deg, ${branding.primary_color} 0%, ${branding.secondary_color} 100%)`
            }}
          >
            {isLoading ? (
              <>
                <span className="login-form__spinner"></span>
                <span>Signing in...</span>
              </>
            ) : (
              tenantLogin ? 'Sign in to workspace' : 'Sign in'
            )}
          </button>
        </form>

        <div className="login-card__footer">
          {anomerQaLogin ? (
            <p>Isolated QA workspace · Dummy data only.</p>
          ) : bioAscentLogin ? (
            <p>Demonstration environment · Synthetic data · Powered by Adroit</p>
          ) : anomerLogin ? (
            <p>Need access? Contact the Anomer Bio team.</p>
          ) : autifonyLogin ? (
            <p>Need access? Contact your Autifony workspace administrator.</p>
          ) : rimeLogin ? (
            <p>Need access? Contact the RIME Therapeutics team.</p>
          ) : irisLogin ? (
            <p>Learn more at <a href="https://www.iris-scientific.com/" target="_blank" rel="noopener noreferrer">iris-scientific.com</a></p>
          ) : nomicLogin ? (
            <p>Need access? Contact your Nomic workspace administrator.</p>
          ) : stereoLogin ? (
            <p>Need access? Contact the Stereo Biotherapeutics team.</p>
          ) : amplitudeLogin ? (
            <p>Need access? Contact the Amplitude Ventures team.</p>
          ) : (
            <p>Don't have an account? <a href="mailto:hello@adroitai.ai?subject=Adroit%20Access%20Request">Request access</a></p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
